import React, { useState } from 'react';
import DefaultLayout from 'admin/components/DefaultLayout';
import { useIntl } from 'react-intl';
import { Button, Tabs, Table, Dropdown, Input, Checkbox, Icon } from 'ui';
import { DownOutlined } from '@ant-design/icons';
import Filter from 'admin/components/Filter';
import { StyledButtonList } from 'styles/buttonList';
import { Pagination } from 'types/Pagination';
import EditCreateUserDrawer from './EditCreateUserDrawer';
import { StyledFarmUserMobileList } from './style';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';
import MobileList from 'admin/components/MobileLayout/MobileList';
import {
  StyledMobileHeaderWrapper,
} from 'admin/components/MobileLayout/style';
import AdminMobileFilterDrawer from 'ui/Drawer/Drawers/AdminMobileFilterDrawer';
import { useBreakpoint } from 'utils/useBreakpoint';
import { MobileListField } from 'ui/Card/MobileListCard';

export interface advancedSearchTypes {
  id?: number;
  full_name?: string;
  email?: string;
  role?: string;
  search?: string;
  page_size?: number;
}

const FarmUserListPage = () => {
  const [advancedSearch, setAdvancedSearch] = useState<advancedSearchTypes>({});
  const [totalCount, setTotalCount] = useState<Pagination>({});
  const [userEditId, setUserEditId] = useState<number>();
  const [reload, setReload] = useState<number>(0);
  const [showCreateEditDrawer, setShowCreateEditDrawer] =
    useState<boolean>(false);
  const [isFarmUserMobileFilterOpen, setIsFarmUserMobileFilterOpen] =
    useState<boolean>(false);

  const intl = useIntl();
  const { isLgUp } = useBreakpoint();

  const resetFilter = () => {
    setAdvancedSearch({});
    setReload((prev) => prev + 1);
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'general.first_name' }),
      dataIndex: 'name',
    },
    {
      title: intl.formatMessage({ id: 'general.surname' }),
      dataIndex: 'surname',
    },
    {
      title: intl.formatMessage({ id: 'general.personal_code' }),
      dataIndex: 'personal_code',
    },
    {
      title: intl.formatMessage({ id: 'general.email' }),
      dataIndex: 'email',
    },
    {
      title: intl.formatMessage({ id: 'general.telephone' }),
      dataIndex: 'phone',
    },
    {
      title: intl.formatMessage({ id: 'general.position' }),
      dataIndex: 'position',
    },
    {
      title: intl.formatMessage({ id: 'general.status' }),
      dataIndex: 'is_active',
      render: (value: boolean) => <Checkbox disabled checked={value} />,
    },
    {
      title: intl.formatMessage({ id: 'general.actions' }),
      dataIndex: 'id',
      render: (id: number) => (
        <Dropdown
          className="actions"
          overlayClassName="status-actions"
          getPopupContainer={(trigger: any) =>
            trigger.parentNode.querySelector('button') as HTMLElement
          }
          menu={{
            items: [
              {
                label: intl.formatMessage({ id: 'general.edit' }),
                key: 'edit',
              },
            ],
            onClick: (value) => {
              if (value.key === 'edit') {
                setUserEditId(id);
                setShowCreateEditDrawer(true);
                return;
              }
            },
          }}
        >
          <Button
            className="action"
            label={intl.formatMessage({ id: 'general.actions' })}
            icon={<DownOutlined />}
            iconPosition="end"
            size="middle"
            ability={'edit_farm_user'}
          />
        </Dropdown>
      ),
    },
  ];

  const mobileItems: {
    coverFields: MobileListField[];
    bodyFields: MobileListField[];
    footerFields: MobileListField[];
  } = {
    coverFields: [
      {
        name: 'name',
        render: (_: string, record: { name?: string; surname?: string }) =>
          `${record.name || ''} ${record.surname || ''}`.trim(),
      },
    ],
    bodyFields: [
      {
        name: 'personal_code',
        label: intl.formatMessage({ id: 'general.personal_code' }),
      },
      {
        name: 'phone',
        label: intl.formatMessage({ id: 'general.telephone' }),
      },
      {
        name: 'email',
        label: intl.formatMessage({ id: 'general.email' }),
      },
      {
        name: 'position',
        label: intl.formatMessage({ id: 'general.position' }),
      },
      {
        name: 'is_active',
        label: intl.formatMessage({ id: 'general.status' }),
        render: (value: boolean) => <Checkbox disabled checked={value} />,
      },
    ],
    footerFields: [
      {
        name: 'actions',
        render: (_: undefined, record: { id: number }) => (
          <Dropdown
            menu={{
              items: [
                {
                  label: intl.formatMessage({ id: 'general.edit' }),
                  key: 'edit',
                },
              ],
              onClick: (value) => {
                if (value.key === 'edit') {
                  setUserEditId(record.id);
                  setShowCreateEditDrawer(true);
                }
              },
            }}
          >
            <Button
              label={intl.formatMessage({ id: 'general.actions' })}
              icon={<DownOutlined />}
              iconPosition="end"
              size="small"
              className="actions"
              ability="edit_farm_user"
            />
          </Dropdown>
        ),
      },
    ],
  };

  const farmUsersFilter = (
    <Filter
      onFinish={setAdvancedSearch}
      isPagination
      totalRecord={totalCount?.total ?? 0}
      reloadFilterTag={reload}
    >
      <Input
        label={intl.formatMessage({ id: 'farm_users.name' })}
        name="name"
      />
      <Input
        label={intl.formatMessage({ id: 'farm_users.surname' })}
        name="surname"
      />
      <Input label={intl.formatMessage({ id: 'general.email' })} name="email" />
      <Input
        label={intl.formatMessage({ id: 'general.search' })}
        name="search"
      />
      <div className={'button-section-container'}>
        <div className={'button-section-wrapper'}>
          <Button
            className={'mobile-button'}
            type="link"
            label={intl.formatMessage({ id: 'general.delete_filter' })}
            onClick={() => resetFilter()}
          />
          <Button
            className={'mobile-button'}
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={() => setIsFarmUserMobileFilterOpen(false)}
          />
          <Button
            type="primary"
            label={
              isLgUp
                ? intl.formatMessage({ id: 'general.select' })
                : intl.formatMessage({ id: 'general.filter_action' })
            }
            htmlType="submit"
            onClick={() => setIsFarmUserMobileFilterOpen(false)}
          />
        </div>
      </div>
    </Filter>
  );

  const items = [
    {
      key: 'farm_users',
      label: intl.formatMessage({ id: 'navigation.users' }),
      children: (
        <ResponsiveWrapper
          desktopComponent={
            <>
              {farmUsersFilter}
              <Table
                url="/api/v2/farm-users"
                columns={columns}
                filter={advancedSearch}
                page_size={advancedSearch.page_size ?? 25}
                paginnationStatus={setTotalCount}
                reload={reload}
              />
            </>
          }
          mobileComponent={
            <StyledFarmUserMobileList>
              <MobileList
                url="/api/v2/farm-users"
                filter={advancedSearch}
                paginnationStatus={(el) => setTotalCount(el)}
                page_size={advancedSearch.page_size ?? 25}
                reload={reload}
                coverFields={mobileItems.coverFields}
                bodyFields={mobileItems.bodyFields}
                footerFields={mobileItems.footerFields}
              />
            </StyledFarmUserMobileList>
          }
        />
      ),
    },
  ];

  return (
    <>
      <StyledMobileHeaderWrapper>
        <Button
          type="text"
          label={`+ ${intl.formatMessage({ id: 'general.create_user' })}`}
          onClick={() => setShowCreateEditDrawer(true)}
          ability="edit_farm_user"
        />
        <Button
          type="text"
          label={intl.formatMessage({ id: 'general.filter' })}
          onClick={() => setIsFarmUserMobileFilterOpen(true)}
          icon={
            <Icon name="admin-filter-icon" className="admin-header-item-icon" />
          }
        />
      </StyledMobileHeaderWrapper>
      <DefaultLayout.PageLayout
        breadcrumbs={[
          {
            title: intl.formatMessage({ id: 'navigation.beginning' }),
          },
          {
            title: intl.formatMessage({ id: 'navigation.users' }),
          },
        ]}
      >
        <EditCreateUserDrawer
          userEditId={userEditId}
          setUserEditId={setUserEditId}
          reload={reload}
          setReload={setReload}
          showCreateEditDrawer={showCreateEditDrawer}
          setShowCreateEditDrawer={setShowCreateEditDrawer}
        />
        <DefaultLayout.PageContent>
          <Tabs
            tabBarExtraContent={
              <StyledButtonList className="hide-mobile">
                <Button
                  label={intl.formatMessage({ id: 'general.create_user' })}
                  type="primary"
                  onClick={() => setShowCreateEditDrawer(true)}
                  ability="edit_farm_user"
                />
              </StyledButtonList>
            }
            items={items}
          />
          <AdminMobileFilterDrawer
            isOpen={isFarmUserMobileFilterOpen}
            onClose={() => setIsFarmUserMobileFilterOpen(false)}
          >
            {farmUsersFilter}
          </AdminMobileFilterDrawer>
        </DefaultLayout.PageContent>
      </DefaultLayout.PageLayout>
    </>
  );
};

export default FarmUserListPage;
